<template>
  <div class="container mt-3">
    <div class="row">
      <div class="col s12">
        <div v-show="!validKey" class="card">
          <div class="card-title primary white-text">
            <i class="material-icons icon">fingerprint</i>
            Invalid key
          </div>

          <div class="card-content">
            Please click the link in your email.
          </div>
        </div>

        <div v-show="requestSent" class="card">
          <div class="card-title primary white-text">
            <i class="material-icons icon">fingerprint</i>
            Reset request sent
          </div>

          <div class="card-content">
            Password reset request sent. You can close this window and check your email for a password reset link.
          </div>
        </div>

        <div v-show="passwordChanged" class="card">
          <div class="card-title primary white-text">
            <i class="material-icons icon">fingerprint</i>
            Password changed
          </div>

          <div class="card-content">
            Your password has been updated. Go to the login page to continue.<br><br>

             <a @click="$router.push('/')" class="btn primary waves-effect waves-light">
              <i class="material-icons right">chevron_right</i>
              Click here to login
            </a>
          </div>
        </div>

        <div v-show="validKey && !requestSent && !passwordChanged" class="card">
          <div class="card-title primary white-text">
            <i class="material-icons icon">fingerprint</i>
            Forgot your password?
          </div>

          <div class="card-content">
            <div class="row">
              <div v-if="stepNumber === 1" class="input-field col s12">
                <input
                  autofocus
                  type="text"
                  id="email"
                  v-model="editedItem.email"
                >
                <label for="email">Your email address</label>
                <span class="helper-text red-text">{{ (formErrors.email !== '') ? formErrors.email : '' }}</span>
              </div>

              <div v-if="stepNumber === 2" class="input-field col s12">
                <input
                  type="password"
                  id="password"
                  v-model="editedItem.password"
                >
                <label for="password">Password</label>
                <span class="helper-text">Secure your account with a strong password</span>
                <span class="helper-text red-text">{{ (formErrors.password !== '') ? formErrors.password : '' }}</span>
              </div>
              <div class="clearfix"></div>
            </div>
          </div>

          <div class="card-action grey lighten-3">
            <a
              class="waves-effect waves-light btn primary right"
              :disabled="saveInProgress"
              @click="resetPassword"
            >
              <div v-if="saveInProgress" class="saving-animation">
                <div class="lds-ripple">
                  <div></div>
                  <div></div>
                </div>
              </div>
              <i v-if="!saveInProgress" class="material-icons right">chevron_right</i>
              {{ stepNumber === 1 ? 'Send Reset Link' : 'Change Password' }}
            </a>
            <div class="clearfix"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Import the API functions
import httpApi from '@/utils/api'

export default {
  props: ['k'],

  data () {
    return {
      stepNumber: 1,

      // Loading status
      loadingAnimation: true,

      uniqueKey: '',

      validKey: true,

      requestSent: false,

      passwordChanged: false,

      saveInProgress: false,

      // Form bound variables being edited
      editedItem: {
        id: 0,
        password: '',
        email: ''
      },

      // Error holders for form fields
      formErrors: {
        password: null,
        email: null
      },

      defaultFormErrors: {
        password: null,
        email: null
      }
    }
  },

  mounted () {
    console.log(this.k)
    // If key is empty, then 1st step - accept user's email
    if (typeof this.k === 'undefined') {
      this.stepNumber = 1
    // If key exists, validate the key, and accept user's new password
    } else {
      this.stepNumber = 2

      this.uniqueKey = this.k

      httpApi.callWithKey({
        url: 'user/resetpassword?k=' + this.uniqueKey,
        method: 'get'
      })
        .then((resp) => {
          // Assign details
          this.editedItem.id = resp.data.userID
        })
        // If an error occurs
        .catch((err) => {
          this.validKey = false

          // Show error message
          this.$store.dispatch('SHOW_STATUS_MESSAGE', {
            color: 'error',
            message: err.message
          })
        })
    }
  },

  methods: {
    resetPassword () {
      this.saveInProgress = true

      // First step, request password reset link
      if (this.stepNumber === 1) {
        // Validate user input data
        let regEmail = /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,})$/
        let valid = true

        if (this.editedItem.email === '') {
          this.formErrors.email = 'Please enter a valid email address'
          valid = false
        } else if (regEmail.test(this.editedItem.email) === false) {
          this.formErrors.email = 'Please enter a valid email address'
          valid = false
        } else {
          this.formErrors.email = ''
        }

        if (valid) {
          let dataBody = {
            email: this.editedItem.email
          }

          // Save to API
          httpApi.callWithKey({
            url: 'user/resetpassword',
            data: dataBody,
            method: 'post'
          })
            .then((resp) => {
              // Hide loading animation
              this.saveInProgress = false

              this.requestSent = true

              // Show error message
              this.$store.dispatch('SHOW_STATUS_MESSAGE', {
                color: 'success',
                message: resp.message
              })
            })
            .catch((err) => {
              // Hide loading animation
              this.saveInProgress = false

              // Empty older errors
              this.formErrors = Object.assign({}, this.defaultFormErrors)

              // Show new errors. Timeout required, else fails
              setTimeout(() => {
                let errors = Object.keys(err.data)
                errors.forEach((value, key) => {
                  // Set error message
                  this.formErrors[value] = err.data[value]
                })
              }, 100)

              // There could be multiple errors in an array. Convert to a string
              let errorString = Object.values(err.data)
              errorString = errorString.join(', ')

              // Show error message
              this.$store.dispatch('SHOW_STATUS_MESSAGE', {
                color: 'error',
                message: errorString
              })
            })
        } else {
          this.saveInProgress = false
        }
      // Second step, use key and userID to change password
      } else {
        // Validate user input data
        let valid = true

        if (this.editedItem.password === '') {
          this.formErrors.password = 'Please enter a password'
          valid = false
        } else {
          this.formErrors.password = ''
        }

        if (valid) {
          let dataBody = {
            userID: this.editedItem.id,
            newPassword: this.editedItem.password,
            resetHashKey: this.uniqueKey
          }

          // Save to API
          httpApi.callWithKey({
            url: 'user/resetpassword',
            data: dataBody,
            method: 'put'
          })
            .then((resp) => {
              // Hide loading animation
              this.saveInProgress = false

              this.passwordChanged = true

              // Show error message
              this.$store.dispatch('SHOW_STATUS_MESSAGE', {
                color: 'success',
                message: resp.message
              })
            })
            .catch((err) => {
              // Hide loading animation
              this.saveInProgress = false

              // Empty older errors
              this.formErrors = Object.assign({}, this.defaultFormErrors)

              // Show new errors. Timeout required, else fails
              setTimeout(() => {
                let errors = Object.keys(err.data)
                errors.forEach((value, key) => {
                  // Set error message
                  this.formErrors[value] = err.data[value]
                })
              }, 100)

              // There could be multiple errors in an array. Convert to a string
              let errorString = Object.values(err.data)
              errorString = errorString.join(', ')

              // Show error message
              this.$store.dispatch('SHOW_STATUS_MESSAGE', {
                color: 'error',
                message: errorString
              })
            })
        } else {
          this.saveInProgress = false
        }
      }
    }
  }
}
</script>
